import apisauce from 'apisauce';

import { isErrorInfo } from '@/shared/interfaces/error-info.interface';
import { getSalesTeamMemberDetailsById } from '@/shared/services/data/sales-management/sales-management.data';
import { userRefreshToken } from '@/shared/services/data/user-data/auth.data';
import {
  cleanupUserData,
  getSessionCorrelationId,
  getUserAccessToken,
  getUserRefreshToken,
  isUserSalesTeam,
  setSessionCorrelationId,
  setUserTeam,
  setUserToken
} from '@/shared/services/helpers/auth-helper/auth-helper.service';

import { SERVER_BASE_URL } from './routes-config';

const createSessionId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const random = Math.floor(Math.random() * 16);
    const value = char === 'x' ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
};

const create = (baseURL = SERVER_BASE_URL) => {
  const api = apisauce.create({
    baseURL,
    headers: {
      'X-Client-Platform': 'internal_admin',
      'X-Client-Type': 'web',
      'Cache-Control': 'no-cache',
      'Content-Type': 'application/json',
      'Accept-Language': '*'
    },
    timeout: 240000
  });
  api.addRequestTransform(async (request) => {
    request.headers = request.headers || {};
    request.httpsAgent = {
      rejectUnauthorized: false
    };
    let sessionCorrelationId = getSessionCorrelationId();
    if (!sessionCorrelationId) {
      sessionCorrelationId = createSessionId();
      setSessionCorrelationId(sessionCorrelationId);
    }
    request.headers['x-session-id'] = sessionCorrelationId;

    const jwt = getUserAccessToken();
    if (jwt) request.headers.Authorization = `Bearer ${jwt}`;
  });
  api.addAsyncResponseTransform(async (response) => {
    if (response?.headers && response?.headers['x-session-id']) {
      setSessionCorrelationId(response?.headers['x-session-id']);
    }
    if (
      response.status === 401 &&
      response.config?.url &&
      response.config?.url?.indexOf('/auth/refreshToken') === -1
    ) {
      const refreshToken = await getUserRefreshToken();
      if (refreshToken) {
        const userData = await userRefreshToken(refreshToken);

        if (isErrorInfo(userData)) {
          // Only cleanup on actual authentication failures (401/403), not network errors
          // Network errors like TIMEOUT_ERROR, NETWORK_ERROR have status === undefined
          if (userData.status === 401 || userData.status === 403) {
            cleanupUserData();
          }
          // Don't retry if it's a network error - user should stay logged in
          return;
        }

        setUserToken({ ...userData, refreshToken });

        if (userData.userId && isUserSalesTeam()) {
          const teamData = await getSalesTeamMemberDetailsById(userData.userId, 'DASHBOARD_MODULE');
          if (isErrorInfo(teamData)) {
            console.log('no team detected');
          } else {
            if (teamData?.data?.team) {
              setUserTeam(teamData?.data?.team);
            }
          }
        }

        const accessToken = userData.accessToken;
        if (accessToken) {
          api.setHeader('Authorization', `Bearer ${accessToken}`);
          // retry original request
          const data = await api.any(response.config as any);
          // replace response data
          response.data = data.data;
          response.status = data.status;
          response.problem = data.problem;
          response.headers = data.headers;
          response.ok = data.ok;
        }
      } else {
        cleanupUserData();
      }
    }
  });

  return api;
};

const api = create();

export default api;
export type ApiType = typeof api;
